import Contracts from '../../../../api/api-contracts';
import { getWebEnv } from '../../../../api/api-env';
import { Fees } from '../../../../api/api-fees';
import Payments from '../../../../api/api-payments';
import { Profile } from '../../../../api/api-profile';
import { logError } from './logging-service';

export const fetchStripeKey = (country, locale) => {
  const stripeKeyMap = {
    us: 'stripe_us_public_key',
    gb: 'stripe_uk_public_key',
    au: 'stripe_au_public_key',
    ca: 'stripe_ca_public_key',
    de: 'stripe_eu_public_key',
    fr: 'stripe_eu_public_key',
    es: 'stripe_eu_public_key'
  };

  return new Promise((resolve, reject) => {
    getWebEnv(locale, (response) => {
      if (response.error || response.statusCode >= 400) {
        logError('payment-service.fetchStripeKey', response);
        reject(response);
        return;
      }
      const countryShort = (country || 'us').toLowerCase();
      const stripePublicKey = response?.stripe?.[stripeKeyMap[countryShort]];

      resolve(stripePublicKey);
    });
  });
};

export const fetchCustomerPaymentMethods = (accessToken, SSOid) => {
  return new Promise((resolve, reject) => {
    Payments.getCustomerData(SSOid, accessToken, (response) => {
      if (response.error || response.statusCode >= 400) {
        logError('payment-service.fetchCustomerPaymentMethods', response);
        reject(response.error);
        return;
      }

      resolve(response?.data);
    });
  });
};

export const fetchCustomerCredits = (accessToken, SSOid) => {
  return new Promise((resolve, reject) => {
    Profile.readCredits(SSOid, accessToken, (response) => {
      if (response?.error || response?.statusCode >= 400) {
        logError('payment-service.fetchCustomerCredits', response);
        reject(response.error);
        return;
      }

      resolve(response?.balance);
    });
  });
};

export const fetchCustomerBillingAddress = (accessToken, SSOid) => {
  return new Promise((resolve, reject) => {
    Fees.fetchAddress(accessToken, SSOid, 'billing', (response) => {
      if (response.error || response.statusCode >= 400) {
        logError('payment-service.fetchCustomerBillingAddress', response);
        reject(response.error);
        return;
      }

      const customerBillingAddress = (response || []).find(
        (address) =>
          address.type === 'BILLING' && address.obj_state === 'ACTIVE'
      );
      resolve(customerBillingAddress);
    });
  });
};

export const createSetupIntent = (accessToken, SSOid, payload) => {
  return new Promise((resolve, reject) => {
    Payments.createSetupIntent(SSOid, accessToken, payload, (response) => {
      if (response.error || response.statusCode >= 400) {
        logError('payment-service.createSetupIntent', response);
        reject(response.error);
        return;
      }
      resolve(response);
    });
  });
};

export const confirmPaymentIntent = (SSOid, paymentIntentId, accessToken, payload) => {
  return new Promise((resolve, reject) => {
    Payments.confirmIntent(SSOid, paymentIntentId, accessToken, payload, (response) => {
      if (response.error || response.statusCode >= 400) {
        logError('payment-service.confirmPaymentIntent', response);
        reject(response.error);
        return;
      }
      resolve(response);
    });
  });
};

export const createStripePaymentMethod = async ({
  stripeInstance,
  stripeElements,
  cardholderName
}) => {
  try {
    const response = await stripeInstance.createPaymentMethod({
      elements: stripeElements,
      params: {
        billing_details: {
          name: cardholderName
        }
      }
    });

    if (response.error || response.statusCode >= 400) {
      throw response.error;
    }

    return response;
  } catch (error) {
    logError('payment-service.createStripePaymentMethod', error);
    throw error;
  }
};

export const pollPaymentProcessingStatus = (
  accessToken,
  bookingId,
  stripeInstance
) => {
  return new Promise((resolve, reject) => {
    const pollProcessingStatus = (pollTicks) => {
      Contracts.readCCAuthState(bookingId, accessToken, (response) => {
        const isAuthStateComplete = response?.count === 0;
        if (isAuthStateComplete) {
          return resolve();
        }

        if (response?.error || response?.statusCode >= 400) {
          return reject(response.error);
        }

        if (pollTicks >= 10) {
          // eslint-disable-next-line prefer-promise-reject-errors
          return reject({ error: 'Payment processing timeout occured' });
        }

        if (!response.client_action_required) {
          // no user action required, continue polling until contracts are resolved
          setTimeout(() => pollProcessingStatus(pollTicks + 1), 3000);
          return;
        }

        // user action required, confirm the payment
        Payments.getBookingClientActionData(
          bookingId,
          accessToken,
          async (clientActionRes) => {
            if (!clientActionRes.client_action_required) {
              setTimeout(() => pollProcessingStatus(1), 1000);
              return;
            }

            try {
              const confirmPaymentRes = await stripeInstance.confirmCardPayment(
                clientActionRes.client_action_client_secret
              );
              if (
                confirmPaymentRes.error ||
                confirmPaymentRes.statusCode >= 400
              ) {
                throw confirmPaymentRes;
              } else {
                // poll again, because we may need to do SCA again
                setTimeout(() => pollProcessingStatus(1), 1000);
              }
            } catch (error) {
              logError('payment-service.pollPaymentMethodAuthState', error);
              reject(error?.error || error);
            }
          }
        );
      });
    };
    pollProcessingStatus(1);
  });
};
